import { useId } from "react";
import type { ReactElement } from "react";
import type { FremdKarte } from "@kundeninfo/api-client";
import { Icon, useSofortPopup } from "@kundeninfo/ui";
import { Chip } from "@kundeninfo/ui/me-design";
import { KiKnopf } from "../../components/meHuellen.js";
import { datumsMarke } from "./ticketFormat.js";
import { FREMD_STATUSART, FREMD_SYSTEM, minutenText } from "./fremdSicht.js";

/**
 * DIE FREMDKARTE (Spec Tickets, Nachtrag 1f, F142): Objektzeilen-Optik wie die Ticketkarte, aber eigene Klasse
 * `tfk` — die Ziehhilfen greifen `.tk`, eine Fremdkarte hat keinen Griff und kein „Verschieben …". Kennung und
 * Titel sind ein Verweis ins Quellsystem (neuer Tab, `noopener noreferrer`, 219). „Fälligkeit …" (Uhr) öffnet
 * den Dialog rechts (F144). Angeheftete eigene Tickets (F150) öffnen das eigene Ticket. Titel und Status aus dem
 * Fremdsystem sind reiner Text.
 */
export function FremdKarteAnsicht(p: { k: FremdKarte; heute: string; onFaelligkeit: (id: string) => void;
                                       onTicket: (ticketId: string) => void }): ReactElement {
  const k = p.k;
  const id = useId();
  const marke = datumsMarke(k.dueDate, p.heute);
  const text = marke.ohne ? "ohne Fälligkeit" : marke.text;
  const quelle = useSofortPopup<HTMLSpanElement>(`${FREMD_SYSTEM[k.system]} · ${k.instanceLabel}`);
  const faellig = useSofortPopup<HTMLSpanElement>("Fälligkeit …");
  return (
    <article className="tfk" data-fremd={k.id} aria-labelledby={`${id}-oeffnen`}>
      {/* Wie die Ticketkarte: Kennung und Titel bekommen die Breite, das Datum steht rechts daneben (Entwurf v001);
          der Quell-Chip führt die Fußzeile an (Sichtprobe Aufgabe 9: im Kopf drückte er den Titel auf ein Wort). */}
      <div className="tfk__kopf">
        <a id={`${id}-oeffnen`} className="tfk__oeffnen" href={k.url} target="_blank" rel="noopener noreferrer">
          <span className="tk__nummer">{k.externalKey}</span> <span className="tk__titel">{k.title}</span>
        </a>
        <span className="tk__datumfeld">
          {marke.art === null
            ? <Chip className="tk__datum">{text}</Chip>
            : <Chip className="tk__datum" statusArt={marke.art}>{text}</Chip>}
        </span>
      </div>
      <div className="tfk__fuss">
        <span ref={quelle} className="tfk__quellefeld"><Chip className="tfk__quelle">{FREMD_SYSTEM[k.system]}</Chip></span>
        <span className="tk__neben">{FREMD_STATUSART[k.remoteStateKind]}</span>
        {k.timeSpentMinutes !== null && k.timeSpentMinutes > 0 && (
          <span className="tk__neben">{minutenText(k.timeSpentMinutes)} erfasst</span>
        )}
        {k.verknuepft.map((v) => (
          <KiKnopf key={v.ticketId} art="leise" className="tfk__ticket" name={`${v.ticketNumber} öffnen`}
            onClick={() => p.onTicket(v.ticketId)}>↔ {v.ticketNumber}</KiKnopf>
        ))}
        <span ref={faellig} className="tfk__aktion">
          <KiKnopf art="leise" className="tfk__faellig" name={`${k.externalKey} Fälligkeit …`} onClick={() => p.onFaelligkeit(k.id)}>
            <Icon name="clock" size={14} />
          </KiKnopf>
        </span>
      </div>
    </article>
  );
}
